import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bot,
  FileSpreadsheet,
  Loader2,
  Lock,
  MessageCircle,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  Workflow,
  Zap,
} from 'lucide-react';
import Button from '../../components/shared/Button';
import Input from '../../components/shared/Input';
import CustomSelect from '../../components/shared/CustomSelect';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';
import { useToastContext } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import useVendorPlanAccess from '../../hooks/useVendorPlanAccess';
import {
  useCreateCustomWorkflow,
  useDeleteCustomWorkflow,
  useToggleWorkspaceAutomation,
  useUpdateCustomWorkflow,
  useVendorWorkspaceAgentRuns,
  useVendorWorkspaceAutomationRuns,
  useVendorWorkspaceAutomations,
  useVendorWorkspaceInsights,
} from '../../services/vendorWorkspaceService';
import { formatDate, formatTime } from '../../utils/dateUtils';

const emptyCustom = () => ({
  title: '',
  when: 'external_lead_created',
  if_channel: '',
  if_min_amount: '',
  if_max_amount: '',
  if_min_days_until_event: '',
  if_max_days_until_event: '',
  if_tag: '',
  then_action: 'create_todo',
  then_title: 'Follow up — {{name}}',
  then_priority: 'normal',
  then_due_hours: 24,
  then_whatsapp_template: 'follow_up',
  then_agent_key: 'lead_qualifier',
  then_lead_status: 'pending',
});

const thenActionLabel = (action) => {
  switch (action) {
    case 'create_meeting':
      return 'meeting';
    case 'draft_whatsapp':
      return 'WhatsApp draft';
    case 'run_agent':
      return 'agent';
    case 'update_lead_stage':
      return 'lead stage';
    default:
      return 'to-do';
  }
};

const INTEGRATION_CARDS = [
  {
    key: 'whatsapp',
    title: 'WhatsApp',
    body: 'Send approved templates from automations and Workspace tasks. Connect a business number when your plan allows.',
    Icon: MessageCircle,
    cap: 'can_whatsapp',
    status: 'Configure on Integrations',
  },
  {
    key: 'excel',
    title: 'Excel / Google Sheets',
    body: 'Live push of Workspace + Bookings tabs into your Google Sheet (Pro). CSV export stays on Workspace (Basic+).',
    Icon: FileSpreadsheet,
    cap: 'can_sheets_live',
    status: 'Sync now on Integrations',
  },
  {
    key: 'agents',
    title: 'Workflow agents',
    body: 'Agents that draft agendas, chase balances, and build event packs into your board.',
    Icon: Bot,
    cap: 'can_agents',
    status: 'Enable on Integrations',
  },
];

const VendorAutomations = () => {
  const { success, error: showError } = useToastContext();
  const { confirm } = useConfirm();
  const { isFreeTier, currentPlanSlug } = useVendorPlanAccess();
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [editingCustomId, setEditingCustomId] = useState(null);
  const [customDraft, setCustomDraft] = useState(emptyCustom);

  const closeCustomForm = () => {
    setShowCustomForm(false);
    setEditingCustomId(null);
    setCustomDraft(emptyCustom());
  };

  const openCreateCustom = () => {
    setEditingCustomId(null);
    setCustomDraft(emptyCustom());
    setShowCustomForm(true);
  };

  const openEditCustom = (wf) => {
    setEditingCustomId(wf.id);
    setCustomDraft({
      title: wf.title || '',
      when: wf.when || 'external_lead_created',
      if_channel: wf.if_channel || '',
      if_min_amount: wf.if_min_amount ?? '',
      if_max_amount: wf.if_max_amount ?? '',
      if_min_days_until_event: wf.if_min_days_until_event ?? '',
      if_max_days_until_event: wf.if_max_days_until_event ?? '',
      if_tag: wf.if_tag || '',
      then_action: wf.then_action || 'create_todo',
      then_title: wf.then_title || 'Follow up — {{name}}',
      then_priority: wf.then_priority || 'normal',
      then_due_hours: wf.then_due_hours ?? 24,
      then_whatsapp_template: wf.then_whatsapp_template || 'follow_up',
      then_agent_key: wf.then_agent_key || 'meeting_prep',
      then_lead_status: wf.then_lead_status || 'pending',
    });
    setShowCustomForm(true);
  };

  const { data: automationsRes, isLoading } = useVendorWorkspaceAutomations();
  const automationsData = automationsRes?.data || {};
  const recipes = automationsData.recipes || [];
  const customs = automationsData.custom_workflows || [];
  const customTriggers = automationsData.custom_triggers || [];
  const customThenActions = automationsData.custom_then_actions || [];
  const whatsappTemplates = automationsData.whatsapp_templates || [
    { value: 'follow_up', label: 'Follow-up' },
    { value: 'meeting', label: 'Meeting confirm' },
    { value: 'payment', label: 'Payment reminder' },
    { value: 'event', label: 'Event check-in' },
  ];
  const agentOptions = automationsData.agent_options || [];
  const leadStageOptions = automationsData.lead_stage_options || [
    { value: 'pending', label: 'Pending' },
    { value: 'meeting_scheduled', label: 'Meeting Scheduled' },
    { value: 'meeting_done', label: 'Meeting Done' },
    { value: 'lead_not_responsive', label: 'Lead Not Responsive' },
    { value: 'lead_cancelled', label: 'Lead Cancelled' },
  ];
  const stats = automationsData.stats || {};
  const unlocked = Boolean(automationsData.unlocked);
  const tierLabel = automationsData.tier_label || (isFreeTier ? 'Free' : currentPlanSlug || 'Plan');
  const caps = {
    can_whatsapp: unlocked,
    can_excel_export: unlocked || ['basic', 'starter', 'growth', 'premium', 'pro', 'enterprise'].includes(
      String(currentPlanSlug || '').toLowerCase()
    ),
    can_sheets_live: unlocked,
    can_agents: unlocked,
  };

  const { data: runsRes, isLoading: runsLoading } = useVendorWorkspaceAutomationRuns({ limit: 40 });
  const runs = runsRes?.data || [];

  const { data: insightsRes } = useVendorWorkspaceInsights({ range: 30 });
  const roi = insightsRes?.data?.roi || {};

  const { data: agentRunsRes, isLoading: agentRunsLoading } = useVendorWorkspaceAgentRuns({
    limit: 15,
  });
  const agentRuns = agentRunsRes?.data || [];

  const toggleAutomation = useToggleWorkspaceAutomation({
    onSuccess: (res) => success(null, res?.message || 'Automation updated.'),
    onError: (err) =>
      showError(
        err?.response?.data?.message || 'Could not update automation',
        err?.response?.data?.upgrade_required ? 'Automations require Pro.' : undefined
      ),
  });

  const createCustom = useCreateCustomWorkflow({
    onSuccess: () => {
      success(null, 'Custom workflow created.');
      closeCustomForm();
    },
    onError: (err) =>
      showError(
        err?.response?.data?.message || 'Could not create workflow',
        err?.response?.data?.upgrade_required ? 'Custom workflows require Pro.' : undefined
      ),
  });

  const updateCustom = useUpdateCustomWorkflow({
    onSuccess: (_res, variables) => {
      // Toggle-only updates keep the form closed; full edits close the builder.
      if (variables && Object.keys(variables).some((k) => k !== 'id' && k !== 'enabled')) {
        success(null, 'Workflow updated.');
        closeCustomForm();
      } else {
        success(null, 'Workflow updated.');
      }
    },
    onError: (err) => showError(null, err?.response?.data?.message || 'Could not update workflow'),
  });

  const deleteCustom = useDeleteCustomWorkflow({
    onSuccess: () => success(null, 'Workflow deleted.'),
    onError: (err) => showError(null, err?.response?.data?.message || 'Could not delete workflow'),
  });

  const triggerLabel = (value) =>
    customTriggers.find((t) => t.value === value)?.label || value;

  return (
    <div className="space-y-6 pb-12">
      <VendorPageHeader
        title="Automations"
        subtitle="Connect recipes that create Workspace tasks from leads, invoices, meetings, and payments — tiered by plan."
        actions={
          <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-ink-secondary">
            <Sparkles className="h-3.5 w-3.5" />
            {tierLabel}
            {!unlocked ? ' · Locked' : ' · Pro automations'}
          </span>
        }
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.automations?.title || 'Automations that stick'}
        steps={
          VENDOR_PAGE_GUIDES.automations?.steps || [
            'Enable a recipe that matches how you sell (leads, invoices, events).',
            'Build a When → If channel → Then board card workflow for your edge cases.',
            'When it fires, tasks land on your Workspace board automatically.',
          ]
        }
        icon={Zap}
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {[
          { label: 'Recipes on', value: recipes.filter((r) => r.enabled).length },
          { label: 'Runs (30d)', value: roi.automation_runs_30d ?? stats.successful_runs_30d ?? 0 },
          { label: 'Hours saved', value: roi.hours_saved_30d ?? 0 },
          { label: 'Error rate', value: `${roi.automation_error_rate_30d ?? 0}%` },
          { label: 'Agent runs', value: roi.agent_runs_30d ?? 0 },
          { label: 'Plan', value: tierLabel },
        ].map((card) => (
          <div key={card.label} className="rounded-product-md border border-border bg-surface p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-tertiary">
              {card.label}
            </p>
            <p className="mt-1 text-xl font-semibold tabular-nums text-ink">{card.value}</p>
          </div>
        ))}
      </div>

      {!unlocked && (
        <div className="rounded-product-md border border-dashed border-border bg-surface-muted/40 p-4 text-sm text-ink-secondary">
          <Lock className="mr-1 inline h-3.5 w-3.5" />
          Recipe toggles need Pro. Preview everything below, then{' '}
          <Link to="/vendor/subscription" className="font-medium text-ink underline-offset-2 hover:underline">
            upgrade
          </Link>{' '}
          to turn them on. Tasks still show on{' '}
          <Link to="/vendor/workspace" className="font-medium text-ink underline-offset-2 hover:underline">
            Workspace
          </Link>
          .
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <section className="space-y-3 xl:col-span-2">
          <h2 className="text-lg font-semibold tracking-tight text-ink">Recipe library</h2>
          <div className="product-surface divide-y divide-border overflow-hidden">
            {isLoading ? (
              <div className="flex justify-center py-14">
                <Loader2 className="h-6 w-6 animate-spin text-ink" />
              </div>
            ) : (
              recipes.map((recipe) => (
                <div
                  key={recipe.recipe_key}
                  className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between"
                >
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Workflow className="h-4 w-4 text-ink-tertiary" />
                      <p className="text-sm font-semibold text-ink">{recipe.title}</p>
                      <span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-medium text-ink-tertiary">
                        {recipe.trigger}
                      </span>
                    </div>
                    <p className="text-sm text-ink-secondary">{recipe.description}</p>
                    <p className="text-xs text-ink-tertiary">
                      Runs: {recipe.run_count ?? 0}
                      {recipe.last_run_at
                        ? ` · Last ${formatDate(recipe.last_run_at)} ${formatTime(recipe.last_run_at)}`
                        : ' · Never run'}
                      {' · '}~{recipe.estimated_minutes_saved ?? 0} min each time
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={!unlocked || toggleAutomation.isPending}
                    onClick={() =>
                      toggleAutomation.mutate({
                        recipeKey: recipe.recipe_key,
                        enabled: !recipe.enabled,
                      })
                    }
                    className={`relative h-7 w-12 shrink-0 rounded-full border transition-colors ${
                      recipe.enabled
                        ? 'border-emerald-600 bg-emerald-600'
                        : 'border-border bg-surface-muted'
                    } disabled:opacity-50`}
                    aria-label={recipe.enabled ? 'Disable' : 'Enable'}
                  >
                    <span
                      className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
                        recipe.enabled ? 'left-6' : 'left-0.5'
                      }`}
                    />
                  </button>
                </div>
              ))
            )}
          </div>

          <div className="flex flex-wrap items-end justify-between gap-2 pt-4">
            <div>
              <h2 className="text-lg font-semibold tracking-tight text-ink">Custom workflows</h2>
              <p className="text-sm text-ink-secondary">
                When → If (channel, amount, days) → Then (board, WhatsApp draft, agent, or lead stage).
              </p>
            </div>
            <Button
              size="sm"
              className="rounded-md"
              icon={showCustomForm ? undefined : Plus}
              disabled={!unlocked}
              onClick={() => (showCustomForm ? closeCustomForm() : openCreateCustom())}
            >
              {showCustomForm ? 'Close builder' : 'New workflow'}
            </Button>
          </div>

          {showCustomForm && unlocked && (
            <form
              className="space-y-3 rounded-md border-2 border-ink bg-[#f3f4f0] p-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (!customDraft.title.trim() || !customDraft.then_title.trim()) {
                  showError('Missing fields', 'Name the workflow and the board card title.');
                  return;
                }
                if (
                  customDraft.then_action === 'update_lead_stage' &&
                  !customDraft.then_lead_status
                ) {
                  showError('Missing stage', 'Pick a lead stage for this Then action.');
                  return;
                }
                const emptyToNull = (v) =>
                  v === '' || v === null || v === undefined ? null : v;
                const payload = {
                  ...customDraft,
                  if_channel: customDraft.if_channel || null,
                  if_min_amount: emptyToNull(customDraft.if_min_amount),
                  if_max_amount: emptyToNull(customDraft.if_max_amount),
                  if_min_days_until_event: emptyToNull(customDraft.if_min_days_until_event),
                  if_max_days_until_event: emptyToNull(customDraft.if_max_days_until_event),
                  if_tag: customDraft.if_tag?.trim() || null,
                  then_due_hours: Number(customDraft.then_due_hours) || 24,
                  then_lead_status:
                    customDraft.then_action === 'update_lead_stage'
                      ? customDraft.then_lead_status
                      : null,
                };
                if (editingCustomId) {
                  updateCustom.mutate({ id: editingCustomId, ...payload });
                } else {
                  createCustom.mutate({ ...payload, enabled: true });
                }
              }}
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink">
                {editingCustomId ? 'Edit When → If → Then' : 'When → If → Then'}
              </p>
              <Input
                label="Workflow name"
                value={customDraft.title}
                onChange={(e) => setCustomDraft((p) => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Chase external leads same day"
              />
              <CustomSelect
                label="When"
                options={
                  customTriggers.length
                    ? customTriggers.map((t) => ({ value: t.value, label: t.label }))
                    : [{ value: 'external_lead_created', label: 'When an external lead is created' }]
                }
                value={customDraft.when}
                onChange={(v) => setCustomDraft((p) => ({ ...p, when: v }))}
              />
              <CustomSelect
                label="If channel"
                options={[
                  { value: '', label: 'Any channel' },
                  { value: 'internal', label: 'Internal only' },
                  { value: 'external', label: 'External only' },
                ]}
                value={customDraft.if_channel}
                onChange={(v) => setCustomDraft((p) => ({ ...p, if_channel: v }))}
              />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input
                  label="If min amount (optional)"
                  type="number"
                  min={0}
                  step="0.01"
                  value={customDraft.if_min_amount}
                  onChange={(e) =>
                    setCustomDraft((p) => ({ ...p, if_min_amount: e.target.value }))
                  }
                  placeholder="e.g. 200"
                />
                <Input
                  label="If max amount (optional)"
                  type="number"
                  min={0}
                  step="0.01"
                  value={customDraft.if_max_amount}
                  onChange={(e) =>
                    setCustomDraft((p) => ({ ...p, if_max_amount: e.target.value }))
                  }
                  placeholder="e.g. 2000"
                />
                <Input
                  label="If min days until event"
                  type="number"
                  min={0}
                  value={customDraft.if_min_days_until_event}
                  onChange={(e) =>
                    setCustomDraft((p) => ({
                      ...p,
                      if_min_days_until_event: e.target.value,
                    }))
                  }
                  placeholder="e.g. 3"
                />
                <Input
                  label="If max days until event"
                  type="number"
                  min={0}
                  value={customDraft.if_max_days_until_event}
                  onChange={(e) =>
                    setCustomDraft((p) => ({
                      ...p,
                      if_max_days_until_event: e.target.value,
                    }))
                  }
                  placeholder="e.g. 14"
                />
              </div>
              <Input
                label="If tag (optional)"
                value={customDraft.if_tag}
                onChange={(e) => setCustomDraft((p) => ({ ...p, if_tag: e.target.value }))}
                placeholder="e.g. vip or vip,wedding"
              />
              <p className="text-[11px] text-ink-tertiary">
                Amount filters use invoice/deposit totals when present. Day filters use booking
                event dates. Tag filters match any tag on the lead (`deal_summary.tags`).
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <CustomSelect
                  label="Then"
                  options={
                    customThenActions.length
                      ? customThenActions.map((t) => ({ value: t.value, label: t.label }))
                      : [
                          { value: 'create_todo', label: 'Create a to-do on the board' },
                          { value: 'create_meeting', label: 'Create a meeting on the board' },
                          {
                            value: 'draft_whatsapp',
                            label: 'Draft a WhatsApp message (human confirm)',
                          },
                          { value: 'run_agent', label: 'Run a workflow agent' },
                          { value: 'update_lead_stage', label: 'Update lead stage' },
                        ]
                  }
                  value={customDraft.then_action}
                  onChange={(v) => setCustomDraft((p) => ({ ...p, then_action: v }))}
                />
                <CustomSelect
                  label="Priority"
                  options={[
                    { value: 'low', label: 'Low' },
                    { value: 'normal', label: 'Normal' },
                    { value: 'high', label: 'High' },
                    { value: 'urgent', label: 'Urgent' },
                  ]}
                  value={customDraft.then_priority}
                  onChange={(v) => setCustomDraft((p) => ({ ...p, then_priority: v }))}
                />
              </div>
              {customDraft.then_action === 'draft_whatsapp' ? (
                <CustomSelect
                  label="WhatsApp template"
                  options={whatsappTemplates}
                  value={customDraft.then_whatsapp_template}
                  onChange={(v) =>
                    setCustomDraft((p) => ({ ...p, then_whatsapp_template: v }))
                  }
                />
              ) : null}
              {customDraft.then_action === 'run_agent' ? (
                <CustomSelect
                  label="Agent"
                  options={
                    agentOptions.length
                      ? agentOptions
                      : [
                          { value: 'meeting_prep', label: 'Meeting prep' },
                          { value: 'payment_chaser', label: 'Payment chaser' },
                          { value: 'event_pack', label: 'Event pack' },
                          { value: 'lead_qualifier', label: 'Lead qualifier' },
                        ]
                  }
                  value={customDraft.then_agent_key}
                  onChange={(v) => setCustomDraft((p) => ({ ...p, then_agent_key: v }))}
                />
              ) : null}
              {customDraft.then_action === 'update_lead_stage' ? (
                <CustomSelect
                  label="Lead stage"
                  options={leadStageOptions}
                  value={customDraft.then_lead_status}
                  onChange={(v) => setCustomDraft((p) => ({ ...p, then_lead_status: v }))}
                />
              ) : null}
              <Input
                label={
                  customDraft.then_action === 'draft_whatsapp'
                    ? 'Draft card title'
                    : customDraft.then_action === 'run_agent' ||
                        customDraft.then_action === 'update_lead_stage'
                      ? 'Label (for your reference)'
                      : 'Board card title'
                }
                value={customDraft.then_title}
                onChange={(e) => setCustomDraft((p) => ({ ...p, then_title: e.target.value }))}
                placeholder="Use {{name}}, {{invoice}}, or {{booking}}"
              />
              {customDraft.then_action !== 'run_agent' &&
              customDraft.then_action !== 'update_lead_stage' ? (
                <Input
                  label="Due in (hours)"
                  type="number"
                  min={1}
                  value={customDraft.then_due_hours}
                  onChange={(e) =>
                    setCustomDraft((p) => ({ ...p, then_due_hours: e.target.value }))
                  }
                />
              ) : (
                <p className="text-[11px] text-ink-tertiary">
                  {customDraft.then_action === 'update_lead_stage'
                    ? 'Moves Internal/External lead status when the trigger is a lead. Invalid transitions create a board note instead.'
                    : 'Agent runs use the linked trigger entity when available. Cards still need your review before any send.'}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  type="submit"
                  className="rounded-md"
                  loading={createCustom.isPending || updateCustom.isPending}
                >
                  {editingCustomId ? 'Update workflow' : 'Save workflow'}
                </Button>
                {editingCustomId ? (
                  <Button
                    type="button"
                    variant="secondary"
                    className="rounded-md"
                    onClick={closeCustomForm}
                  >
                    Cancel
                  </Button>
                ) : null}
              </div>
            </form>
          )}

          <div className="product-surface divide-y divide-border overflow-hidden">
            {customs.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-ink-secondary">
                No custom workflows yet. Build a When → If → Then rule above.
              </p>
            ) : (
              customs.map((wf) => (
                <div
                  key={wf.id}
                  className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between"
                >
                  <div className="min-w-0 space-y-1">
                    <p className="text-sm font-semibold text-ink">{wf.title}</p>
                    <p className="text-xs text-ink-secondary">
                      <span className="font-medium text-ink">When</span> {triggerLabel(wf.when)}
                      {' · '}
                      <span className="font-medium text-ink">If</span>{' '}
                      {[
                        wf.if_channel || 'any channel',
                        wf.if_min_amount != null && wf.if_min_amount !== ''
                          ? `min $${wf.if_min_amount}`
                          : null,
                        wf.if_max_amount != null && wf.if_max_amount !== ''
                          ? `max $${wf.if_max_amount}`
                          : null,
                        wf.if_min_days_until_event != null &&
                        wf.if_min_days_until_event !== ''
                          ? `≥${wf.if_min_days_until_event}d`
                          : null,
                        wf.if_max_days_until_event != null &&
                        wf.if_max_days_until_event !== ''
                          ? `≤${wf.if_max_days_until_event}d`
                          : null,
                        wf.if_tag ? `tag:${wf.if_tag}` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                      {' · '}
                      <span className="font-medium text-ink">Then</span>{' '}
                      {thenActionLabel(wf.then_action)}
                      {wf.then_action === 'draft_whatsapp' && wf.then_whatsapp_template
                        ? ` (${wf.then_whatsapp_template})`
                        : ''}
                      {wf.then_action === 'run_agent' && wf.then_agent_key
                        ? ` (${wf.then_agent_key.replace(/_/g, ' ')})`
                        : ''}
                      {wf.then_action === 'update_lead_stage' && wf.then_lead_status
                        ? ` → ${wf.then_lead_status.replace(/_/g, ' ')}`
                        : ''}
                      : {wf.then_title}
                    </p>
                    <p className="text-xs text-ink-tertiary">
                      Runs: {wf.run_count ?? 0}
                      {wf.last_run_at
                        ? ` · Last ${formatDate(wf.last_run_at)} ${formatTime(wf.last_run_at)}`
                        : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={!unlocked || updateCustom.isPending}
                      onClick={() =>
                        updateCustom.mutate({ id: wf.id, enabled: !wf.enabled })
                      }
                      className={`relative h-7 w-12 shrink-0 rounded-full border transition-colors ${
                        wf.enabled
                          ? 'border-emerald-600 bg-emerald-600'
                          : 'border-border bg-surface-muted'
                      } disabled:opacity-50`}
                      aria-label={wf.enabled ? 'Disable' : 'Enable'}
                    >
                      <span
                        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
                          wf.enabled ? 'left-6' : 'left-0.5'
                        }`}
                      />
                    </button>
                    <button
                      type="button"
                      disabled={!unlocked}
                      className="rounded-md border border-border p-1.5 text-ink-tertiary hover:bg-surface-muted hover:text-ink disabled:opacity-50"
                      aria-label="Edit workflow"
                      onClick={() => openEditCustom(wf)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      className="rounded-md border border-border p-1.5 text-ink-tertiary hover:bg-surface-muted hover:text-ink"
                      onClick={async () => {
                        const ok = await confirm({
                          title: 'Delete custom workflow?',
                          message: `“${wf.title}” will stop running. Past board cards stay.`,
                          confirmLabel: 'Delete',
                          variant: 'danger',
                        });
                        if (ok) deleteCustom.mutate(wf.id);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        <aside className="space-y-6">
          <section className="space-y-3">
            <h2 className="text-lg font-semibold tracking-tight text-ink">Connections</h2>
            <p className="text-xs text-ink-secondary">
              Where automations can reach — WhatsApp, sheets, and agents. Tiered by plan.
            </p>
            <div className="space-y-2">
              {INTEGRATION_CARDS.map(({ key, title, body, Icon, cap, status }) => {
                const on = Boolean(caps[cap]);
                return (
                  <div
                    key={key}
                    className={`rounded-product-md border p-3 ${
                      on
                        ? 'border-border bg-surface'
                        : 'border-dashed border-border bg-surface-muted/40'
                    }`}
                  >
                    <div className="mb-1 flex items-center gap-2">
                      <Icon className="h-4 w-4 text-ink-tertiary" />
                      <p className="text-sm font-semibold text-ink">{title}</p>
                      {!on && (
                        <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-ink-tertiary">
                          <Lock className="h-3 w-3" />
                          Upgrade
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-ink-secondary">{body}</p>
                    <p className="mt-1.5 text-[11px] text-ink-tertiary">{status}</p>
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/vendor/integrations"
                className="inline-flex text-xs font-medium text-ink underline-offset-2 hover:underline"
              >
                Open Integrations →
              </Link>
              <Link
                to="/vendor/subscription"
                className="inline-flex text-xs font-medium text-ink underline-offset-2 hover:underline"
              >
                Compare plans →
              </Link>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold tracking-tight text-ink">Recipe run log</h2>
            <div className="product-surface overflow-hidden">
              {runsLoading ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="h-5 w-5 animate-spin text-ink" />
                </div>
              ) : runs.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-ink-secondary">
                  No runs yet. Enable a recipe and wait for a trigger.
                </p>
              ) : (
                <ul className="divide-y divide-border max-h-[280px] overflow-y-auto">
                  {runs.map((run) => (
                    <li key={run.id} className="px-4 py-3">
                      <p className="text-sm font-medium text-ink">{run.title || run.recipe_key}</p>
                      <p className="mt-0.5 text-xs text-ink-tertiary">
                        <span
                          className={
                            run.status === 'success'
                              ? 'text-emerald-700'
                              : run.status === 'failed'
                                ? 'text-red-700'
                                : ''
                          }
                        >
                          {run.status}
                        </span>
                        {run.created_at
                          ? ` · ${formatDate(run.created_at)} ${formatTime(run.created_at)}`
                          : ''}
                        {run.result?.created_item_ids?.length
                          ? ` · ${run.result.created_item_ids.length} task(s)`
                          : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold tracking-tight text-ink">Agent run log</h2>
            <div className="product-surface overflow-hidden">
              {agentRunsLoading ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="h-5 w-5 animate-spin text-ink" />
                </div>
              ) : agentRuns.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-ink-secondary">
                  No agent runs yet. Use Ask agent on Workspace.
                </p>
              ) : (
                <ul className="divide-y divide-border max-h-[220px] overflow-y-auto">
                  {agentRuns.map((run) => (
                    <li key={run.id} className="px-4 py-3">
                      <p className="text-sm font-medium text-ink">{run.title || run.agent_key}</p>
                      <p className="mt-0.5 text-xs text-ink-tertiary">
                        <span
                          className={
                            run.status === 'success'
                              ? 'text-emerald-700'
                              : run.status === 'failed'
                                ? 'text-red-700'
                                : ''
                          }
                        >
                          {run.status}
                        </span>
                        {run.created_at
                          ? ` · ${formatDate(run.created_at)} ${formatTime(run.created_at)}`
                          : ''}
                        {run.result?.created_item_ids?.length
                          ? ` · ${run.result.created_item_ids.length} card(s)`
                          : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <Link
              to="/vendor/workspace"
              className="inline-flex text-xs font-medium text-ink underline-offset-2 hover:underline"
            >
              Open Workspace · Ask agent →
            </Link>
          </section>
        </aside>
      </div>
    </div>
  );
};

export default VendorAutomations;
